import React, { useEffect, useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import Button, { ButtonType } from "../Assets/Button";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";

interface ICustomField {
  customFieldName: string;
  customFieldValue: number;
}

interface RepaymentChargesModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;

  values?: {
    fileCharge?: number;
    portalCharge?: number;
    customFields?: ICustomField[];
  };

  onSubmit: (data: {
    fileCharge: number;
    portalCharge: number;
    customFields?: ICustomField[];
  }) => Promise<void>;
}

const RepaymentChargesModal: React.FC<RepaymentChargesModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  values,
}) => {
  const handleWheelPrevent = usePreventWheelChange();

  const [fileCharge, setFileCharge] = useState<string>("0");
  const [portalCharge, setPortalCharge] = useState<string>("0");

  const [customFields, setCustomFields] = useState<ICustomField[]>([]);

  useEffect(() => {
    if (isOpen) {
      setFileCharge(
        values?.fileCharge !== undefined ? values.fileCharge.toString() : "0",
      );

      setPortalCharge(
        values?.portalCharge !== undefined
          ? values.portalCharge.toString()
          : "0",
      );

      setCustomFields(values?.customFields || []);
    }
  }, [isOpen, values]);

  if (!isOpen) return null;

  const addCustomField = () => {
    setCustomFields((prev) => [
      ...prev,
      {
        customFieldName: "",
        customFieldValue: 0,
      },
    ]);
  };

  const removeCustomField = (index: number) => {
    setCustomFields((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCustomField = (
    index: number,
    key: keyof ICustomField,
    value: string | number,
  ) => {
    setCustomFields((prev) =>
      prev.map((field, i) =>
        i === index
          ? {
              ...field,
              [key]: value,
            }
          : field,
      ),
    );
  };

  const handleSubmit = async () => {
    await onSubmit({
      fileCharge: Number(fileCharge || 0),
      portalCharge: Number(portalCharge || 0),
      customFields: customFields.map((field) => ({
        customFieldName: field.customFieldName,
        customFieldValue: Number(field.customFieldValue || 0),
      })),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* HEADER */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-800">
            Update Repayment Charges
          </h2>

          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1 rounded-lg hover:bg-gray-100 transition-all"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* BODY */}
        <div className="p-5 overflow-y-auto">
          {/* FILE CHARGE */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Charge (₹)
            </label>

            <input
              type="number"
              value={fileCharge}
              onChange={(e) => setFileCharge(e.target.value)}
              onWheel={handleWheelPrevent}
              className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter file charge"
              min="0"
            />
          </div>

          {/* PORTAL CHARGE */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Portal Charge (₹)
            </label>

            <input
              type="number"
              value={portalCharge}
              onChange={(e) => setPortalCharge(e.target.value)}
              onWheel={handleWheelPrevent}
              className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter portal charge"
              min="0"
            />
          </div>

          {/* CUSTOM FIELDS */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">
                Custom Fields
              </h3>

              <button
                type="button"
                onClick={addCustomField}
                className="flex items-center gap-2 text-sm bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-all"
              >
                <Plus className="w-4 h-4" />
                Add Field
              </button>
            </div>

            <div className="space-y-3">
              {customFields.map((field, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl p-4 bg-gray-50"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Field Name
                      </label>

                      <input
                        type="text"
                        value={field.customFieldName}
                        onChange={(e) =>
                          updateCustomField(
                            index,
                            "customFieldName",
                            e.target.value,
                          )
                        }
                        placeholder="Ex: TDS"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Value (₹)
                      </label>

                      <input
                        type="number"
                        value={field.customFieldValue}
                        onChange={(e) =>
                          updateCustomField(
                            index,
                            "customFieldValue",
                            Number(e.target.value),
                          )
                        }
                        onWheel={handleWheelPrevent}
                        placeholder="Enter value"
                        min="0"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end mt-3">
                    <button
                      type="button"
                      onClick={() => removeCustomField(index)}
                      className="flex items-center gap-1 text-red-500 hover:text-red-600 text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-100 bg-gray-50">
          <Button
            type={ButtonType.Cancel}
            onClick={onClose}
            disabled={isLoading}
            className="text-xs"
          >
            Cancel
          </Button>

          <Button
            type={ButtonType.Submit}
            onClick={handleSubmit}
            disabled={isLoading}
            className="text-xs"
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RepaymentChargesModal;
